/**
 * Roles Partner: claves fijas (coinciden con `partner_roles.role` en Supabase).
 * `label`, `desc`, `capabilities` y `emoji` son solo UX; no renombrar `key` sin migración.
 */

/** Funciones compartidas por cualquier partner aprobado (referencia de producto). */
export const PARTNER_ROLE_COMMON_CAPABILITIES = [
  'Perfil público partner (logo, descripción, web, redes sociales, ubicación)',
  'Estado de cuenta visible (pendiente / aprobado / suspendido)',
  'Notificaciones de operaciones y mensajes',
  'Valoraciones recibidas y respuesta pública',
  'Mensajes directos con DreamVinyl',
];

export const PARTNER_ROLE_DEFS = [
  {
    key: 'store',
    emoji: '🎵',
    label: 'Tienda de Música',
    desc: 'Venta verificada, custodia del disco y logística con usuarios.',
    icon: 'store',
    capabilities: [
      'Publicar anuncios de venta con badge «Tienda verificada»',
      'Gestionar su catálogo (activar / pausar / retirar anuncios)',
      'Recibir discos de otros usuarios para verificar estado',
      'Gestionar estados del disco en custodia: esperando → recibido → en revisión → verificado → listo → entregado',
      'Historial de operaciones de custodia',
      'Gestionar entregas y recogidas entre usuarios (punto de recogida opcional)',
      'Confirmar recepción y entrega con código',
      'Activar o desactivar disponibilidad como punto de recogida',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Aparecer en «Descubre lugares»',
      'Valoraciones con sistema de surcos y respuesta pública',
      'Mensajes con DreamVinyl',
      'Estadísticas de perfil y catálogo',
    ],
  },
  {
    key: 'label',
    emoji: '💿',
    label: 'Sello discográfico',
    desc: 'Discografía oficial, novedades y venta de ediciones especiales.',
    icon: 'disc',
    capabilities: [
      'Publicar discografía oficial del sello',
      'Marcar lanzamientos como «Novedad» o «Próximamente»',
      'Vender ediciones especiales en el marketplace con badge «Sello oficial»',
      'Gestionar sus anuncios (activar / pausar / retirar)',
      'Enviar noticias sobre lanzamientos (revisión DreamVinyl)',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Vincular artistas del sello a su perfil',
      'Mensajes con DreamVinyl',
      'Estadísticas de perfil y catálogo',
    ],
  },
  {
    key: 'promoter',
    emoji: '🎪',
    label: 'Promotor y eventos',
    desc: 'Eventos, asistentes, entradas externas y comunicación con la comunidad.',
    icon: 'calendar',
    capabilities: [
      'Enviar eventos (revisión DreamVinyl)',
      'Ver estado de cada evento: borrador → pendiente → aprobado → destacado → finalizado',
      'Gestionar asistentes confirmados e interesados',
      'Añadir enlace externo de entradas (visible solo tras aprobación DreamVinyl)',
      'Enviar noticias sobre eventos (revisión DreamVinyl)',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Mensajes con DreamVinyl',
      'Estadísticas de eventos y perfil',
    ],
  },
  {
    key: 'supplier',
    emoji: '🎸',
    label: 'Proveedor musical',
    desc: 'Equipamiento, accesorios e instrumentos en el marketplace.',
    icon: 'card',
    capabilities: [
      'Publicar productos en el marketplace (tocadiscos, accesorios, instrumentos)',
      'Gestionar su catálogo (activar / pausar / retirar)',
      'Badge «Proveedor verificado» en sus anuncios',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Aparecer en el directorio de partners',
      'Mensajes con DreamVinyl',
      'Estadísticas de perfil y catálogo',
    ],
  },
  {
    key: 'pickup',
    emoji: '📦',
    label: 'Punto DreamVinyl',
    desc: 'Recepción de envíos, códigos de recogida y disponibilidad en directorio.',
    icon: 'map',
    capabilities: [
      'Ver paquetes asignados pendientes de recepción',
      'Confirmar recepción del paquete del vendedor',
      'Confirmar recogida por el comprador con código',
      'Estados: asignado → recibido en punto → disponible → entregado',
      'Activar o desactivar disponibilidad del punto',
      'Configurar horario de atención visible en el directorio',
      'Historial de operaciones completadas',
      'Mensajes con DreamVinyl',
    ],
  },
  {
    key: 'venue',
    emoji: '🎶',
    label: 'Cultura y ocio',
    desc: 'Ficha de local, agenda, géneros y eventos con música en vivo.',
    icon: 'leisure',
    capabilities: [
      'Ficha pública del local en «Descubre lugares»',
      'Gestionar galería de fotos del espacio',
      'Configurar géneros musicales del local (filtrables por usuarios)',
      'Configurar horarios y días de música en vivo',
      'Enviar agenda y eventos (revisión DreamVinyl)',
      'Ver estado de cada evento publicado',
      'Añadir enlace de entradas (visible solo tras aprobación DreamVinyl)',
      'Valoraciones con sistema de surcos y respuesta pública',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Mensajes con DreamVinyl',
      'Estadísticas de perfil y local',
    ],
  },
  {
    key: 'artist',
    emoji: '🎤',
    label: 'Artista',
    desc: 'Perfil verificado, discografía propia y vínculo con sellos del directorio.',
    icon: 'user',
    capabilities: [
      'Perfil público de artista verificado con badge «Artista verificado»',
      'Publicar discografía propia en el marketplace',
      'Gestionar sus anuncios (activar / pausar / retirar)',
      'Enviar noticias sobre lanzamientos, giras y novedades (revisión DreamVinyl)',
      'Enviar publicaciones a Comunidad (revisión DreamVinyl)',
      'Vincular su música a sellos del directorio',
      'Aparecer en el directorio de partners',
      'Valoraciones con sistema de surcos y respuesta pública',
      'Mensajes con DreamVinyl',
      'Estadísticas de perfil',
    ],
  },
  {
    key: 'advertiser',
    emoji: '📢',
    label: 'Anunciante',
    desc: 'Creatividades y campañas publicitarias revisadas por DreamVinyl.',
    icon: 'globe',
    capabilities: [
      'Enviar creatividades publicitarias (revisión DreamVinyl)',
      'Ver estado de cada campaña: borrador → pendiente → aprobado → activo → finalizado',
      'Ver estadísticas de campaña (impresiones, clics)',
      'Gestionar sus campañas activas',
      'Mensajes con DreamVinyl',
      'Sin acceso al marketplace ni a publicación de contenido',
    ],
  },
];

export const PARTNER_ROLE_LABELS = Object.fromEntries(PARTNER_ROLE_DEFS.map(r => [r.key, r.label]));
